import type { AdminSchemas } from "@10twenty/api/admin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { api } from "../api";
import { when } from "../accounts";
import { useMe } from "../components/Shell";
import {
  canRemoveMessage,
  canRemovePhoto,
  conversationLabel,
  messageStateLabel,
  photoStateLabel,
  REASON_LABELS,
  reportStatusLabel,
  targetLabel,
} from "../moderation";
import { errorText, unwrap } from "../problem";

type Report = AdminSchemas["ReportDetail"];

// Opening a report is audited (`report.open`), so it is never refetched by
// itself; an action reloads it once.
export function ReportPage() {
  const { reportId = "" } = useParams();
  const report = useQuery({
    queryKey: ["report", reportId],
    queryFn: async () => unwrap(await api.GET("/reports/{reportId}", { params: { path: { reportId } } })),
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // Each request is audited: a failure is shown, never retried by itself.
    retry: false,
    refetchOnReconnect: false,
  });
  if (report.isPending) return <p>Loading…</p>;
  if (report.isError) return <p className="error">{errorText(report.error)}</p>;
  return <ReportDetail report={report.data} />;
}

function ReportDetail({ report }: { report: Report }) {
  const target = report.target;
  return (
    <>
      <p>
        <Link to="/moderation">← Moderation</Link>
      </p>
      <h1>
        {REASON_LABELS[report.reason]} report about {report.about_message ? "a message" : "an account"}
      </h1>
      <table className="facts">
        <tbody>
          <Fact label="Status">
            {reportStatusLabel(report)}
            {report.resolved_at && <span className="muted"> · {when(report.resolved_at)}</span>}
          </Fact>
          <Fact label="Reported">{when(report.created_at)}</Fact>
          <Fact label="Reported account">
            {target.user_id ? <Link to={`/users/${target.user_id}`}>{targetLabel(target)}</Link> : targetLabel(target)}
            {target.suspended && <span className="muted"> (suspended)</span>}
          </Fact>
          {target.user_id && <Fact label="Open reports about them">{report.target_open_reports}</Fact>}
          <Fact label="Reported by">
            {report.reporter ? (
              <Link to={`/users/${report.reporter.user_id}`}>{report.reporter.name}</Link>
            ) : (
              "Deleted account"
            )}
          </Fact>
          <Fact label="Note">{report.note ? <span className="prewrap">{report.note}</span> : "—"}</Fact>
        </tbody>
      </table>
      {report.message && (
        <section className="stack">
          <h2>The message, as reported</h2>
          <p className="muted">
            {conversationLabel(report.message)} · from {report.message.sender_name ?? "a deleted account"} ·{" "}
            {when(report.message.sent_at)}
            {report.message.edited_at ? " (edited before the report)" : ""} · {messageStateLabel(report.message.state)}
          </p>
          <blockquote className="reported prewrap">{report.message.body}</blockquote>
        </section>
      )}
      {report.photo && (
        <section className="stack">
          <h2>Their photo, as reported</h2>
          <p className="muted">{photoStateLabel(report.photo)}</p>
          {report.photo.data_url ? (
            <img
              className="reported-photo"
              src={report.photo.data_url}
              alt={`Profile photo of ${targetLabel(target)}`}
            />
          ) : (
            <p className="muted">The photo can't be shown (media storage is off or the file is gone).</p>
          )}
        </section>
      )}
      <Actions report={report} />
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <tr>
      <th>{label}</th>
      <td>{children}</td>
    </tr>
  );
}

function Actions({ report }: { report: Report }) {
  const queryClient = useQueryClient();
  const me = useMe().data;
  const [notice, setNotice] = useState<string | null>(null);
  const path = { params: { path: { reportId: report.id } } };
  const done = (text: string) => {
    setNotice(text);
    void queryClient.invalidateQueries({ queryKey: ["report", report.id] });
  };
  const removeMessage = useMutation({
    mutationFn: async () => unwrap(await api.POST("/reports/{reportId}/remove-message", { ...path, body: {} })),
    onSuccess: (result) =>
      done(
        result.already_removed
          ? "The message was already removed."
          : `Message removed for everyone; ${result.reports_closed} ${result.reports_closed === 1 ? "report" : "reports"} closed.`,
      ),
  });
  const dismiss = useMutation({
    mutationFn: async () => unwrap(await api.POST("/reports/{reportId}/dismiss", { ...path, body: {} })),
    onSuccess: (result) => done(result.already_dismissed ? "Already dismissed." : "Dismissed."),
  });
  const suspend = useMutation({
    mutationFn: async (userId: string) =>
      unwrap(await api.POST("/users/{userId}/suspend", { params: { path: { userId } }, body: {} })),
    onSuccess: (result) =>
      done(
        `Account suspended; ${result.sessions_revoked} ${result.sessions_revoked === 1 ? "session" : "sessions"} revoked and open reports about them closed.`,
      ),
  });
  const removePhoto = useMutation({
    mutationFn: async (userId: string) =>
      unwrap(
        await api.POST("/users/{userId}/remove-avatar", {
          params: { path: { userId } },
          body: { report_id: report.id },
        }),
      ),
    onSuccess: () => done("Photo removed for everyone; this report is closed. They may upload another."),
  });
  const busy = removeMessage.isPending || dismiss.isPending || suspend.isPending || removePhoto.isPending;
  const error = removeMessage.error ?? dismiss.error ?? suspend.error ?? removePhoto.error;
  const target = report.target;
  const own = target.user_id !== null && target.user_id === me?.user_id;

  function confirmRemove() {
    if (window.confirm("Remove this message for everyone? Its text stays here as evidence.")) removeMessage.mutate();
  }
  function confirmSuspend(userId: string) {
    const text = `Suspend ${targetLabel(target)}? They are signed out everywhere, their API tokens stop working, sign-in is refused and every open report about them is closed.`;
    if (window.confirm(text)) suspend.mutate(userId);
  }
  function confirmRemovePhoto(userId: string) {
    const text = `Remove ${targetLabel(target)}'s profile photo for everyone? It stays here while another open report keeps it.`;
    if (window.confirm(text)) removePhoto.mutate(userId);
  }
  function confirmDismiss() {
    if (window.confirm("Dismiss this report without action?")) dismiss.mutate();
  }

  const removable = canRemoveMessage(report.message);
  const photoRemovable = canRemovePhoto(report) && !own;
  const suspendable = target.user_id !== null && !target.suspended && !own;
  const dismissable = report.status === "open";
  return (
    <section className="stack">
      <h2>Actions</h2>
      <p className="muted">Every action is recorded in the audit log.</p>
      {notice && <p>{notice}</p>}
      {error && <p className="error">{errorText(error)}</p>}
      {removable || photoRemovable || suspendable || dismissable ? (
        <div className="actions">
          {removable && (
            <button type="button" onClick={confirmRemove} disabled={busy}>
              Remove message
            </button>
          )}
          {photoRemovable && (
            <button type="button" onClick={() => confirmRemovePhoto(target.user_id!)} disabled={busy}>
              Remove photo
            </button>
          )}
          {suspendable && (
            <button type="button" onClick={() => confirmSuspend(target.user_id!)} disabled={busy}>
              Suspend account
            </button>
          )}
          {dismissable && (
            <button type="button" className="button-quiet" onClick={confirmDismiss} disabled={busy}>
              Dismiss
            </button>
          )}
        </div>
      ) : (
        <p className="muted">Nothing left to do here.</p>
      )}
      {own && <p className="muted">This report is about your own account; another moderator should handle it.</p>}
    </section>
  );
}
